import {FormControl, FormGroup, ReactiveFormsModule} from "@angular/forms";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef} from "@angular/material/dialog";
import {ChangeDetectionStrategy, Component, Inject, Injectable, signal} from "@angular/core";
import {lyttTilEsc} from "felles-ng2/støttefunksjoner/tastaturhendelser";
import {MeldingTilBruker} from "felles/tjenester/MeldingTilBruker";
import {i18n, Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";
import {GruppeId, GruppeMedlemListeModell, MedlemId, MedlemÅpneTilgangSpesifikasjon, MøteRessursClient} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {GruppemedlemDatatjeneste} from "felles-ng2/tjenester/datalager/GruppemedlemDatatjeneste";
import {TranslateModule} from "@ngx-translate/core";
import {MatButtonModule} from "@angular/material/button";
import {MatSlideToggle} from "@angular/material/slide-toggle";
import {MatTooltip} from "@angular/material/tooltip";
import {VisSpinnerDirektiv} from "web-ng2/delt/direktiver/button--visSpinner/button--visSpinner";
import {lagrerSignal} from "felles-ng2/tjenester/LagrerIndikator";

const debug = initDebugger('MedlemGjenåpneTilgangDialogKomponent');

export type MedlemGjenåpneTilgangDialogRespons = boolean;


@Component({
    selector: 'medlem-gjenaapnetilgang-dialog',
    templateUrl: 'medlem-gjenåpnetilgang-dialog.html',
    styleUrl: 'medlem-gjenåpnetilgang-dialog.scss',
    host: { 'class': 'e2e-aapnemedlemtilgangdialog' },
    imports: [
        MatDialogModule,
        ReactiveFormsModule,
        MatButtonModule,
        MatSlideToggle,
        MatTooltip,
        TranslateModule,
        VisSpinnerDirektiv,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MedlemGjenåpneTilgangDialogKomponent {

    static vis(dialog: MatDialog, medlem: GruppeMedlemListeModell): MatDialogRef<MedlemGjenåpneTilgangDialogKomponent, MedlemGjenåpneTilgangDialogRespons> {
        return dialog.open(MedlemGjenåpneTilgangDialogKomponent, {data: {medlem} as DialogParametre, disableClose: true, ariaLabelledBy: 'medlem-gjenåpnetilgang-dialog-tittel'});
    }

    medlem!: GruppeMedlemListeModell;

    skjema = new FormGroup({
        oppdaterMoetemal: new FormControl(true, {nonNullable: true}),
        skalSignereSomStandard: new FormControl(true, {nonNullable: true}),
        sendInnkallingTilMoeter: new FormControl(true, {nonNullable: true})
    });
    lagrer = lagrerSignal();
    antallMoeterDetSkalSendesInnkallingTilMoeter = signal(0);

    constructor(@Inject(MAT_DIALOG_DATA) parametre: DialogParametre,
                private avhengigheter: MedlemGjenåpneTilgangDialogKomponentAvhengigheter,
                private meldingTilBruker: MeldingTilBruker,
                private oversettelser: Oversettelser,
                public dialogRef: MatDialogRef<MedlemGjenåpneTilgangDialogKomponent, MedlemGjenåpneTilgangDialogRespons>) {
        //dialog er konf med disableClose:true for å unngå at klikk utenfor dialog lukker denne, ønsker fortsatt at esc skal lukke
        lyttTilEsc(() => this.avbryt());

        this.medlem = parametre.medlem;
        this.avhengigheter.finnIkkeAvholdteMøterSomNyeMedlemmmerKanLeggesTil(this.medlem.gruppeId).then (
          antall => {
              this.antallMoeterDetSkalSendesInnkallingTilMoeter.set(antall);
              antall > 0 ? this.skjema.controls.sendInnkallingTilMoeter.enable() : this.skjema.controls.sendInnkallingTilMoeter.disable();
          }
        );
        this.skjema.controls.oppdaterMoetemal.valueChanges.subscribe(opppdater => {opppdater && this.antallMoeterDetSkalSendesInnkallingTilMoeter() > 0 ? this.skjema.controls.sendInnkallingTilMoeter.enable():this.skjema.controls.sendInnkallingTilMoeter.disable()})
    }

    avbryt() {
        this.dialogRef.close(false);
    }

    async gjenaapne() {
        await this.lagrer.utfør(async () => {
            await this.avhengigheter.åpneTilgang(this.medlem.id, this.somSpesifikasjon());
            this.meldingTilBruker.infomelding(this.oversettelser.oversett(i18n('Tilgang til gruppe åpnet for {navnEllerEpost}'), {navnEllerEpost: this.medlem.navn || this.medlem.epost}));
            this.dialogRef.close(true);
        });
    }

    //hjelpemetoder

    private somSpesifikasjon(): MedlemÅpneTilgangSpesifikasjon {
        return {
            oppdaterMoetemal: this.skjema.value.oppdaterMoetemal!,
            //vi eksponerer ikke oppdaterFremtidigeMoeter-valget men lar det styres av oppdaterMoetemal
            oppdaterFremtidigeMoeter: this.skjema.value.oppdaterMoetemal!,
            skalSignereSomStandard: this.skjema.value.skalSignereSomStandard!,
            sendInnkallingTilMoeter: this.skjema.value.sendInnkallingTilMoeter!
        };
    }
}

export interface DialogParametre {
    medlem: GruppeMedlemListeModell;
}

@Injectable()
export class MedlemGjenåpneTilgangDialogKomponentAvhengigheter {

    constructor(private gruppemedlemDatatjeneste: GruppemedlemDatatjeneste, private møteRessurs:MøteRessursClient) {
    }

    async åpneTilgang(id: MedlemId, spesifikasjon: MedlemÅpneTilgangSpesifikasjon): Promise<void> {
        await this.gruppemedlemDatatjeneste.åpneTilgang(id, spesifikasjon);
    }
    async finnIkkeAvholdteMøterSomNyeMedlemmmerKanLeggesTil(gruppeId: GruppeId): Promise<number> {
        const møter = await this.møteRessurs.finnIkkeAvholdteMøterSomNyeMedlemmmerKanLeggesTil({gruppeId:gruppeId}).somPromise();
        return møter.filter(m => m.moeteInnkallingSendt).length;
    }
}
